import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filterOnBallotNationalStates } from '@/utils/ballotStatus';
import useApi from './useApi';

/**
 * State-level snapshot returned by `GET /ballot/national`.
 *
 * The shape mirrors what the snapshot script writes to the
 * `national_ballot_snapshot` Cosmos container: one document per state (50
 * states + DC) with statewide-only contests and ballot measures.
 */
export interface NationalElection {
  id?: string;
  date: string;
  description: string;
  type: string;
  guidancePhase: string;
  contests: Array<{
    id: string;
    name: string;
    title?: string;
    level?: string;
    branch?: string;
    districtName?: string;
    districtType?: string;
    contestType?: string;
    ocdId?: string;
    seatsUpForElection?: number;
    partisanPrimary?: string | null;
    candidates?: Array<unknown> | null;
  }>;
  ballotMeasures: Array<{
    id?: string;
    name?: string;
    shortName?: string;
    streamlinedName?: string;
    summary?: string | null;
    ballotQuestion?: string | null;
    yesVote?: string;
    noVote?: string;
    ocdId?: string;
    [key: string]: unknown;
  }>;
  voting_dates?: Record<string, unknown>;
  pollingLocationUrl?: string | null;
  website?: string | null;
}

export interface NationalStateSnapshot {
  id: string;
  state_code: string;
  elections: NationalElection[];
  snapshot_date: string;
  source: string;
  updatedAt?: string;
}

interface NationalBallotResponse {
  success: boolean;
  states: NationalStateSnapshot[];
}

/**
 * Fetches the public national blank-ballot snapshot. Auto-fires on mount.
 *
 * Powers the guest experience and signed-in users without an address. The
 * endpoint is unauthenticated, so `useApi`'s auth-aware query is fine — it
 * just won't attach an access token when the user is signed out.
 */
export const useNationalBallot = () => {
  const [states, setStates] = useState<NationalStateSnapshot[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const query = useApi();
  const { t } = useTranslation('ballot');

  const fetchNationalBallot = useCallback(async () => {
    // Clear ``error`` at the start so a manual ``refetch()`` after a
    // failure isn't gated by the (states === null && !error) auto-fire
    // guard below — and so the auto-fire guard can re-trigger if the
    // caller resets state directly.
    setIsLoading(true);
    setError(null);
    try {
      const response = await query('/ballot/national', { method: 'GET' });
      const data: NationalBallotResponse = await response.json();
      if (data.success && Array.isArray(data.states)) {
        setStates(filterOnBallotNationalStates(data.states));
      } else {
        setError(t('error.snapshotUnavailable'));
      }
    } catch (err) {
      console.error('National ballot fetch failed:', err);
      setError(t('error.fetchFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [query, t]);

  useEffect(() => {
    if (states === null && !isLoading && !error) {
      fetchNationalBallot();
    }
  }, [states, isLoading, error, fetchNationalBallot]);

  return { states, isLoading, error, refetch: fetchNationalBallot };
};

export default useNationalBallot;
